import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getProjectGroups,
  createProjectGroup,
  updateProjectGroup,
  deleteProjectGroup,
  reorderProjectGroups,
  assignProjectToGroup,
  bulkAssignProjectsToGroup,
  getAllProjectGroups,
} from '../../services/projects/ProjectGroupService';
import { useAccount } from '../../contexts/account/AccountContexts';

const PROJECT_GROUPS_KEY = ['project-groups'];

export const useProjectGroups = () => {
  return useQuery({
    queryKey: PROJECT_GROUPS_KEY,
    queryFn: getProjectGroups,
  });
};

export const useCreateProjectGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, color }: { name: string; color?: string }) =>
      createProjectGroup(name, color),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECT_GROUPS_KEY });
    },
  });
};

export const useUpdateProjectGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: number; data: { name?: string; color?: string } }) =>
      updateProjectGroup(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECT_GROUPS_KEY });
    },
  });
};

export const useDeleteProjectGroup = (onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: number) => deleteProjectGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECT_GROUPS_KEY });
      onSuccessCallback?.();
    },
  });
};

export const useReorderProjectGroups = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: number[]) => reorderProjectGroups(orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECT_GROUPS_KEY });
    },
  });
};

export const useAssignProjectToGroup = () => {
  const queryClient = useQueryClient();
  const { setProjectResponse } = useAccount();
  return useMutation({
    mutationFn: ({ projectId, groupId }: { projectId: number; groupId: number | null }) =>
      assignProjectToGroup(projectId, groupId),
    onSuccess: (_data, { projectId, groupId }) => {
      queryClient.invalidateQueries({ queryKey: PROJECT_GROUPS_KEY });
      // Local patch instead of full account refetch — avoids the visible "page reload"
      // effect that refetchData() caused by re-rendering ProjectsPage with a brand-new
      // projects array. Mirrors the pattern documented in UseChangeName.
      setProjectResponse((prev) =>
        prev ? prev.map((p) => (p.id === projectId ? { ...p, projectGroupId: groupId } : p)) : prev
      );
    },
  });
};

export const useBulkAssignProjectsToGroup = () => {
  const queryClient = useQueryClient();
  const { setProjectResponse } = useAccount();
  return useMutation({
    mutationFn: ({ projectIds, groupId }: { projectIds: number[]; groupId: number | null }) =>
      bulkAssignProjectsToGroup(projectIds, groupId),
    onSuccess: (_data, { projectIds, groupId }) => {
      queryClient.invalidateQueries({ queryKey: PROJECT_GROUPS_KEY });
      const ids = new Set(projectIds);
      setProjectResponse((prev) =>
        prev ? prev.map((p) => (ids.has(p.id) ? { ...p, projectGroupId: groupId } : p)) : prev
      );
    },
  });
};

export const useAllProjectGroups = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['all-project-groups'],
    queryFn: getAllProjectGroups,
    enabled,
  });
};
